import {
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
  AutoAwesome as MagicIcon,
  Bolt as BoltIcon,
  Diamond as DiamondIcon,
  FlashOn as FlashIcon,
  Psychology as BrainIcon,
  Rocket as RocketIcon,
  Stars as StarsIcon,
  TrendingUp as TrendingIcon,
  Whatshot as FireIcon
} from '@mui/icons-material';
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Stack,
  Typography,
  Fade,
  Paper
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// Import images
import backgroundImage from '../assets/background.jpeg';
import backgroundImage2 from '../assets/background2.jpeg';
import heroImage1 from '../assets/flow.webp';
import heroImage2 from '../assets/cozum.webp';
import heroImage3 from '../assets/versiyon.png';

const HeroRevolution = () => {
  const containerRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [lastManualChange, setLastManualChange] = useState(0);
  const [particles, setParticles] = useState([]);
  const [animationKey, setAnimationKey] = useState(0);

  // Sophisticated slide data
  const slides = [
    {
      id: 1,
      theme: "innovation",
      background: backgroundImage,
      title: "Dijital İnovasyon",
      subtitle: "Geleceği Bugün Yaşayın",
      description: "Yapay zeka destekli araçlar ve modern tasarım anlayışı ile projelerinizi hayata geçirin. Sınırsız yaratıcılık için tasarlanmış platform.",
      primaryColor: "#6366f1",
      secondaryColor: "#8b5cf6",
      accentColor: "#06b6d4",
      image: heroImage1,
      icon: <BrainIcon />,
      features: [
        { title: "AI Destekli Editör", desc: "Akıllı önerilerle hızlı tasarım", icon: <MagicIcon />, color: "#6366f1" },
        { title: "Gerçek Zamanlı İşbirliği", desc: "Takım çalışması yeniden tanımlandı", icon: <FlashIcon />, color: "#8b5cf6" },
        { title: "Bulut Entegrasyonu", desc: "Her yerden güvenli erişim", icon: <DiamondIcon />, color: "#06b6d4" }
      ],
      stats: [
        { value: "50K+", label: "Aktif Kullanıcı", icon: "👥" },
        { value: "99.9%", label: "Uptime Garantisi", icon: "⚡" },
        { value: "24/7", label: "Destek Hizmeti", icon: "🚀" },
        { value: "A+", label: "Güvenlik Skoru", icon: "🛡️" }
      ],
      buttonText: "Keşfetmeye Başla",
      secondaryText: "Canlı Demo"
    },
    {
      id: 2,
      theme: "creativity",
      background: backgroundImage2,
      title: "Yaratıcı Atölye",
      subtitle: "İlhamınızı Özgürleştirin",
      description: "Sürükle-bırak editör, zengin şablon kütüphanesi ve profesyonel araçlar. Hayal ettiğiniz her tasarımı kolayca gerçekleştirin.",
      primaryColor: "#f59e0b",
      secondaryColor: "#ef4444",
      accentColor: "#10b981",
      image: heroImage2,
      icon: <FireIcon />,
      features: [
        { title: "Sürükle-Bırak Editör", desc: "Kod bilmeden profesyonel tasarım", icon: <BoltIcon />, color: "#f59e0b" },
        { title: "Premium Şablonlar", desc: "1000+ hazır tasarım şablonu", icon: <StarsIcon />, color: "#ef4444" },
        { title: "Canlı Önizleme", desc: "Değişiklikleri anında görün", icon: <TrendingIcon />, color: "#10b981" }
      ],
      stats: [
        { value: "1000+", label: "Tasarım Şablonu", icon: "🎨" },
        { value: "∞", label: "Yaratıcılık Sınırı", icon: "🌟" },
        { value: "<1s", label: "Render Hızı", icon: "⚡" },
        { value: "4K", label: "Ultra HD Kalite", icon: "💎" }
      ],
      buttonText: "Yaratmaya Başla",
      secondaryText: "Şablonları Gör"
    },
    {
      id: 3,
      theme: "success",
      background: backgroundImage,
      title: "Başarı Hikayeniz",
      subtitle: "Hedeflerinize Ulaşın",
      description: "Gelişmiş analitik araçlar, performans raporları ve otomatik optimizasyon. Projelerinizin başarısını ölçün ve artırın.",
      primaryColor: "#059669",
      secondaryColor: "#0891b2",
      accentColor: "#7c3aed",
      image: heroImage3,
      icon: <DiamondIcon />,
      features: [
        { title: "Akıllı Analitik", desc: "Detaylı performans raporları", icon: <TrendingIcon />, color: "#059669" },
        { title: "Otomatik SEO", desc: "Arama motorlarında üst sıralarda", icon: <RocketIcon />, color: "#0891b2" },
        { title: "A/B Test Araçları", desc: "Sürekli optimizasyon", icon: <MagicIcon />, color: "#7c3aed" }
      ],
      stats: [
        { value: "300%", label: "Performans Artışı", icon: "📈" },
        { value: "#1", label: "Sektör Lideri", icon: "🏆" },
        { value: "24h", label: "Hızlı Kurulum", icon: "⚡" },
        { value: "∞", label: "Büyüme Potansiyeli", icon: "🚀" }
      ],
      buttonText: "Başarıya Katıl",
      secondaryText: "Başarı Hikayeleri"
    }
  ];

  const currentSlideData = slides[currentSlide];

  // Random animation functions for variety
  const getRandomAnimation = (animations, seed) => {
    const index = (seed + animationKey) % animations.length;
    return animations[index];
  };

  const getIconAnimation = () => {
    const animations = ['iconBounceIn', 'iconSpinIn', 'iconZoomIn', 'iconFlipIn', 'iconSlideIn'];
    return getRandomAnimation(animations, currentSlide);
  };

  const getTitleAnimation = () => {
    const animations = ['titleSoftFade', 'titleGentleRise', 'titleSmoothScale'];
    return getRandomAnimation(animations, currentSlide * 2);
  };

  const getSubtitleAnimation = () => {
    const animations = ['subtitleSoftSlide', 'subtitleGentleFade', 'subtitleSmoothGrow'];
    return getRandomAnimation(animations, currentSlide * 3);
  };

  const getFeatureAnimation = (index) => {
    const animations = ['featureSoftRise', 'featureGentleFade', 'featureSmoothSlide'];
    const selectedAnimation = getRandomAnimation(animations, currentSlide + index);
    return `${selectedAnimation} 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.3 + index * 0.15}s both`;
  };

  // Sophisticated particles
  useEffect(() => {
    const initParticles = [];
    const colors = [currentSlideData.primaryColor, currentSlideData.secondaryColor, currentSlideData.accentColor];
    
    for (let i = 0; i < 25; i++) {
      initParticles.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 3 + 1,
        speedX: (Math.random() - 0.5) * 0.3,
        speedY: (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.6 + 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
        pulse: Math.random() * 2
      });
    }
    setParticles(initParticles);
  }, [currentSlide, currentSlideData.primaryColor, currentSlideData.secondaryColor, currentSlideData.accentColor]);

  // Animate particles
  useEffect(() => {
    const animateParticles = () => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        x: (particle.x + particle.speedX + 100) % 100,
        y: (particle.y + particle.speedY + 100) % 100,
        opacity: 0.2 + Math.sin(Date.now() * 0.001 + particle.pulse) * 0.3
      })));
    };

    const interval = setInterval(animateParticles, 100);
    return () => clearInterval(interval);
  }, []);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMousePosition({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100
        });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Navigation
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setAnimationKey(prev => prev + 1);
    handleManualNavigation();
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setAnimationKey(prev => prev + 1);
    handleManualNavigation();
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setAnimationKey(prev => prev + 1);
    handleManualNavigation();
  };

  const handleManualNavigation = () => {
    setIsAutoPlaying(false);
    setLastManualChange(Date.now());
  };

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setAnimationKey(prev => prev + 1);
    }, 5000);

    return () => clearInterval(timer);
  }, [isAutoPlaying, slides.length]);

  // Resume auto-play
  useEffect(() => {
    if (!isAutoPlaying && lastManualChange > 0) {
      const resumeTimer = setTimeout(() => {
        setIsAutoPlaying(true);
      }, 8000);

      return () => clearTimeout(resumeTimer);
    }
  }, [lastManualChange, isAutoPlaying]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        background: `
          linear-gradient(
            135deg,
            rgba(0, 0, 0, 0.8) 0%,
            rgba(15, 15, 30, 0.85) 30%,
            rgba(30, 15, 45, 0.8) 70%,
            rgba(0, 0, 0, 0.8) 100%
          ),
          url(${currentSlideData.background})
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        display: 'flex',
        alignItems: 'center',
        color: 'white',
        mt: { xs: 8, md: 8 },
        transition: 'background-image 1s ease-in-out'
      }}
    >
      {/* Sophisticated particles */}
      {particles.map((particle) => (
        <Box
          key={particle.id}
          sx={{
            position: 'absolute',
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${particle.color}${Math.floor(particle.opacity * 255).toString(16).padStart(2, '0')}, transparent)`,
            pointerEvents: 'none',
            animation: 'particleDance 4s ease-in-out infinite',
            animationDelay: `${particle.id * 0.1}s`,
            boxShadow: `0 0 ${particle.size * 3}px ${particle.color}40`
          }}
        />
      ))}

      {/* Dynamic gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              ellipse at ${mousePosition.x}% ${mousePosition.y}%,
              ${currentSlideData.primaryColor}15 0%,
              ${currentSlideData.secondaryColor}08 40%,
              transparent 70%
            )
          `,
          pointerEvents: 'none',
          transition: 'background 0.6s ease'
        }}
      />

      {/* Elegant geometric shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '15%',
          right: '10%',
          width: 250,
          height: 250,
          background: `conic-gradient(from 0deg, ${currentSlideData.primaryColor}30, ${currentSlideData.secondaryColor}20, ${currentSlideData.accentColor}25, transparent)`,
          borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
          animation: 'elegantMorph 15s ease-in-out infinite',
          filter: 'blur(2px)',
          opacity: 0.7,
          transform: `translate(${mousePosition.x * 0.02}px, ${mousePosition.y * 0.02}px)`,
          transition: 'background 0.8s ease, transform 0.3s ease'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '8%',
          width: 180,
          height: 180,
          background: `linear-gradient(135deg, ${currentSlideData.accentColor}25, ${currentSlideData.primaryColor}20)`,
          borderRadius: '50%',
          animation: 'elegantFloat 12s ease-in-out infinite',
          filter: 'blur(1px)',
          opacity: 0.5,
          transition: 'background 0.8s ease'
        }}
      />

              {/* Particle System */}
              <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 10% 20%, rgba(255, 255, 255, 0.8) 1px, transparent 1px),
              radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.6) 1px, transparent 1px),
              radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.4) 1px, transparent 1px),
              radial-gradient(circle at 90% 10%, rgba(255, 255, 255, 0.7) 1px, transparent 1px),
              radial-gradient(circle at 20% 90%, rgba(255, 255, 255, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '200px 200px, 300px 300px, 150px 150px, 250px 250px, 180px 180px',
            animation: 'float 20s ease-in-out infinite, rotate 40s linear infinite',
            opacity: 0.6,
            pointerEvents: 'none'
          }}
        />

        

              {/* Dynamic Spotlight Effect */}
              <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(
              ellipse at ${mousePosition.x}% ${mousePosition.y}%,
              rgba(255, 255, 255, 0.15) 0%,
              rgba(255, 255, 255, 0.08) 25%,
              rgba(255, 255, 255, 0.04) 50%,
              transparent 70%
            )`,
            pointerEvents: 'none',
            transition: 'background 0.2s ease-out',
            mixBlendMode: 'overlay'
          }}
        />

      {/* Premium navigation */}
      <IconButton
        onClick={prevSlide}
        sx={{
          position: 'absolute',
          left: { xs: 20, md: 40 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(20px)',
          color: 'white',
          width: 65,
          height: 65,
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 3,
          boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
          '&:hover': {
            bgcolor: `${currentSlideData.primaryColor}20`,
            transform: 'translateY(-50%) scale(1.05)',
            borderColor: `${currentSlideData.primaryColor}40`,
            boxShadow: `0 12px 35px ${currentSlideData.primaryColor}30`
          },
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <ArrowBackIcon sx={{ fontSize: 24 }} />
      </IconButton>

      <IconButton
        onClick={nextSlide}
        sx={{
          position: 'absolute',
          right: { xs: 20, md: 40 },
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 10,
          bgcolor: 'rgba(255,255,255,0.08)',
          backdropFilter: 'blur(20px)',
          color: 'white',
          width: 65,
          height: 65,
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 3,
          boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
          '&:hover': {
            bgcolor: `${currentSlideData.primaryColor}20`,
            transform: 'translateY(-50%) scale(1.05)',
            borderColor: `${currentSlideData.primaryColor}40`,
            boxShadow: `0 12px 35px ${currentSlideData.primaryColor}30`
          },
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <ArrowForwardIcon sx={{ fontSize: 24 }} />
      </IconButton>

      {/* Main content */}
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 5 }}>
        <Grid container spacing={6} alignItems="center" sx={{ minHeight: '85vh' }}>
          {/* Text content */}
          <Grid item xs={12} lg={6}>
            <Fade in={true} timeout={800} key={`content-${animationKey}`}>
              <Box>
                {/* Animated icon */}
                <Box
                  sx={{
                    display: 'inline-block',
                    mb: 4,
                    p: 3,
                    background: `linear-gradient(135deg, ${currentSlideData.primaryColor}20, ${currentSlideData.accentColor}15)`,
                    borderRadius: '50%',
                    border: `2px solid ${currentSlideData.primaryColor}30`,
                    boxShadow: `0 0 30px ${currentSlideData.primaryColor}30`,
                    animation: `${getIconAnimation()} 0.6s ease-out, iconFloat 3s ease-in-out infinite 0.8s`
                  }}
                  key={`icon-${animationKey}`}
                >
                  <Box
                    sx={{
                      color: currentSlideData.primaryColor,
                      fontSize: '2.5rem',
                      filter: `drop-shadow(0 0 15px ${currentSlideData.primaryColor}50)`
                    }}
                  >
                    {currentSlideData.icon}
                  </Box>
                </Box>

                {/* Main title */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '3.2rem', sm: '4.2rem', md: '5.5rem', lg: '6.5rem' },
                    fontWeight: 900,
                    mb: 3,
                    lineHeight: 0.9,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    letterSpacing: '-0.02em',
                    background: `linear-gradient(135deg, #ffffff 0%, ${currentSlideData.primaryColor}40 50%, #ffffff 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: '0 0 60px rgba(255, 255, 255, 0.3)',
                    position: 'relative',
                    animation: `${getTitleAnimation()} 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)`,
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `linear-gradient(90deg, transparent 0%, ${currentSlideData.primaryColor}30 50%, transparent 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      animation: 'titleShimmer 2s ease-in-out infinite 1s'
                    }
                  }}
                  key={`title-${animationKey}`}
                >
                  {currentSlideData.title}
                </Typography>

                {/* Subtitle */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem', lg: '4.8rem' },
                    fontWeight: 700,
                    mb: 4,
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    background: `linear-gradient(135deg, ${currentSlideData.primaryColor} 0%, ${currentSlideData.secondaryColor} 50%, ${currentSlideData.accentColor} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: `0 0 40px ${currentSlideData.primaryColor}40`,
                    animation: `${getSubtitleAnimation()} 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s both, subtitleGlow 3s ease-in-out infinite 1.5s`
                  }}
                  key={`subtitle-${animationKey}`}
                >
                  {currentSlideData.subtitle}
                </Typography>

                {/* Description */}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    fontWeight: 400,
                    mb: 6,
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: 1.8,
                    maxWidth: 650,
                    position: 'relative',
                    animation: 'descriptionSoftFade 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.5s both',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: -20,
                      top: 0,
                      width: 3,
                      height: '100%',
                      background: `linear-gradient(180deg, ${currentSlideData.primaryColor} 0%, ${currentSlideData.accentColor} 100%)`,
                      borderRadius: 2,
                      animation: 'borderPulse 2s ease-in-out infinite 0.8s'
                    }
                  }}
                  key={`desc-${animationKey}`}
                >
                  {currentSlideData.description}
                </Typography>

                {/* Features */}
                <Grid container spacing={3} sx={{ mb: 6 }}>
                  {currentSlideData.features.map((feature, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                      <Box
                        sx={{
                          animation: getFeatureAnimation(index)
                        }}
                        key={`feature-${animationKey}-${index}`}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            background: `linear-gradient(135deg, ${feature.color}12, rgba(255,255,255,0.06))`,
                            backdropFilter: 'blur(15px)',
                            border: `1px solid ${feature.color}25`,
                            borderRadius: 4,
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background: `linear-gradient(90deg, transparent, ${feature.color}30, transparent)`,
                              animation: `featureShine 3s ease-in-out infinite ${index * 0.5}s`
                            },
                            '&:hover': {
                              transform: 'translateY(-8px) scale(1.02)',
                              borderColor: `${feature.color}50`,
                              boxShadow: `0 15px 35px ${feature.color}25`,
                              background: `linear-gradient(135deg, ${feature.color}18, rgba(255,255,255,0.08))`
                            },
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          <Box
                            sx={{
                              color: feature.color,
                              fontSize: '1.8rem',
                              mb: 2,
                              filter: `drop-shadow(0 0 10px ${feature.color}50)`
                            }}
                          >
                            {feature.icon}
                          </Box>
                          <Typography variant="h6" fontWeight={700} color="white" sx={{ mb: 1 }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="rgba(255,255,255,0.8)">
                            {feature.desc}
                          </Typography>
                        </Paper>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {/* Action buttons */}
                <Box
                  sx={{
                    animation: `buttonsSoftRise 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.6s both`
                  }}
                  key={`buttons-${animationKey}`}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<RocketIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${currentSlideData.primaryColor} 0%, ${currentSlideData.secondaryColor} 100%)`,
                        color: 'white',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        fontWeight: 700,
                        px: { xs: 6, md: 8 },
                        py: { xs: 3, md: 3.5 },
                        fontSize: { xs: '1.1rem', md: '1.2rem' },
                        borderRadius: 50,
                        textTransform: 'none',
                        boxShadow: `0 15px 35px ${currentSlideData.primaryColor}40`,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                          animation: 'buttonShine 2.5s ease-in-out infinite'
                        },
                        '&:hover': {
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: `0 20px 45px ${currentSlideData.primaryColor}50`,
                          background: `linear-gradient(135deg, ${currentSlideData.secondaryColor} 0%, ${currentSlideData.primaryColor} 100%)`
                        },
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      {currentSlideData.buttonText}
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<StarsIcon />}
                      sx={{
                        borderColor: `${currentSlideData.primaryColor}60`,
                        color: 'white',
                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                        fontWeight: 600,
                        px: { xs: 6, md: 8 },
                        py: { xs: 3, md: 3.5 },
                        fontSize: { xs: '1.1rem', md: '1.2rem' },
                        borderRadius: 50,
                        textTransform: 'none',
                        borderWidth: 2,
                        backdropFilter: 'blur(15px)',
                        '&:hover': {
                          borderColor: currentSlideData.primaryColor,
                          bgcolor: `${currentSlideData.primaryColor}18`,
                          transform: 'translateY(-4px) scale(1.02)',
                          boxShadow: `0 15px 35px ${currentSlideData.primaryColor}30`
                        },
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      {currentSlideData.secondaryText}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Fade>
          </Grid>

          {/* Visual content */}
          <Grid item xs={12} lg={6}>
            <Fade in={true} timeout={1000} key={`visual-${animationKey}`}>
              <Box
                sx={{
                  position: 'relative',
                  textAlign: 'center'
                }}
              >
                {/* Main image */}
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-block',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: -30,
                      background: `radial-gradient(ellipse at 30% 30%, ${currentSlideData.primaryColor}60 0%, transparent 50%), radial-gradient(ellipse at 70% 70%, ${currentSlideData.secondaryColor}50 0%, transparent 50%)`,
                      borderRadius: '50%',
                      filter: 'blur(20px)',
                      opacity: 0.8,
                      animation: 'imageGlowPulse 4s ease-in-out infinite'
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: -20,
                      background: `linear-gradient(45deg, ${currentSlideData.accentColor}30, transparent 30%, ${currentSlideData.primaryColor}20, transparent 70%, ${currentSlideData.secondaryColor}25)`,
                      borderRadius: 6,
                      filter: 'blur(10px)',
                      opacity: 0.6,
                      animation: 'imageShimmer 6s ease-in-out infinite'
                    }
                  }}
                >
                  <Box
                    component="img"
                    src={currentSlideData.image}
                    alt={currentSlideData.title}
                    sx={{
                      width: '100%',
                      maxWidth: { xs: 400, md: 500 },
                      height: 'auto',
                      borderRadius: 6,
                      boxShadow: `0 25px 60px ${currentSlideData.primaryColor}35, 0 12px 30px ${currentSlideData.secondaryColor}20`,
                      border: `2px solid ${currentSlideData.primaryColor}30`,
                      transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: `perspective(1000px) rotateY(${(mousePosition.x - 50) * 0.08}deg) rotateX(${(mousePosition.y - 50) * -0.05}deg)`,
                      '&:hover': {
                        transform: `perspective(1000px) rotateY(${(mousePosition.x - 50) * 0.15}deg) rotateX(${(mousePosition.y - 50) * -0.08}deg) scale(1.02)`,
                        boxShadow: `0 35px 80px ${currentSlideData.primaryColor}45, 0 18px 40px ${currentSlideData.secondaryColor}30`
                      }
                    }}
                  />
                </Box>

                {/* Stats grid */}
                <Grid container spacing={3} sx={{ mt: 5 }}>
                  {currentSlideData.stats.map((stat, index) => (
                    <Grid item xs={6} md={3} key={index}>
                      <Box
                        sx={{
                          animation: `statsSoftFade 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${0.8 + index * 0.1}s both`
                        }}
                        key={`stat-${animationKey}-${index}`}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2.5,
                            textAlign: 'center',
                            background: `linear-gradient(135deg, ${currentSlideData.primaryColor}10, ${currentSlideData.accentColor}08, rgba(255,255,255,0.05))`,
                            backdropFilter: 'blur(15px)',
                            border: `1px solid ${currentSlideData.primaryColor}20`,
                            borderRadius: 4,
                            '&:hover': {
                              transform: 'translateY(-8px) scale(1.05)',
                              borderColor: `${currentSlideData.primaryColor}40`,
                              boxShadow: `0 15px 35px ${currentSlideData.primaryColor}25`
                            },
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontSize: { xs: '1.8rem', md: '2.2rem' },
                              fontWeight: 800,
                              color: currentSlideData.primaryColor,
                              mb: 0.5,
                              filter: `drop-shadow(0 0 15px ${currentSlideData.primaryColor}50)`
                            }}
                          >
                            {stat.icon} {stat.value}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="rgba(255,255,255,0.85)"
                            fontWeight={600}
                            sx={{ fontSize: '0.9rem' }}
                          >
                            {stat.label}
                          </Typography>
                        </Paper>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Fade>
          </Grid>
        </Grid>
      </Container>

      {/* Elegant slide indicators */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 35,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 2.5,
          zIndex: 10
        }}
      >
        {slides.map((_, index) => (
          <Box
            key={index}
            onClick={() => goToSlide(index)}
            sx={{
              width: currentSlide === index ? 45 : 15,
              height: 15,
              borderRadius: 8,
              background: currentSlide === index 
                ? `linear-gradient(135deg, ${currentSlideData.primaryColor}, ${currentSlideData.secondaryColor})`
                : 'rgba(255,255,255,0.4)',
              cursor: 'pointer',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              border: currentSlide === index 
                ? `1px solid ${currentSlideData.primaryColor}60`
                : '1px solid rgba(255,255,255,0.3)',
              boxShadow: currentSlide === index 
                ? `0 8px 20px ${currentSlideData.primaryColor}40`
                : '0 4px 10px rgba(0,0,0,0.2)',
              '&:hover': {
                transform: 'scale(1.2)',
                boxShadow: `0 10px 25px ${currentSlideData.primaryColor}50`
              }
            }}
          />
        ))}
      </Box>

      {/* Sophisticated CSS animations */}
      <style jsx global>{`
        @keyframes particleDance {
          0%, 100% { transform: translateY(0px) scale(1) rotate(0deg); }
          33% { transform: translateY(-10px) scale(1.1) rotate(120deg); }
          66% { transform: translateY(5px) scale(0.9) rotate(240deg); }
        }

        @keyframes elegantMorph {
          0%, 100% {
            border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
            transform: rotate(0deg) scale(1);
          }
          25% {
            border-radius: 58% 42% 75% 25% / 76% 46% 54% 24%;
            transform: rotate(90deg) scale(1.05);
          }
          50% {
            border-radius: 50% 50% 33% 67% / 55% 27% 73% 45%;
            transform: rotate(180deg) scale(0.95);
          }
          75% {
            border-radius: 33% 67% 58% 42% / 63% 68% 32% 37%;
            transform: rotate(270deg) scale(1.05);
          }
        }

        @keyframes elegantFloat {
          0%, 100% { transform: translateY(0px) translateX(0px) scale(1); }
          50% { transform: translateY(-25px) translateX(10px) scale(1.1); }
        }

        @keyframes iconFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg) scale(1); }
          50% { transform: translateY(-8px) rotate(5deg) scale(1.05); }
        }

        @keyframes titleShimmer {
          0% { transform: translateX(-200%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(200%); }
        }

        @keyframes subtitleGlow {
          0%, 100% { text-shadow: 0 0 40px currentColor; }
          50% { text-shadow: 0 0 60px currentColor, 0 0 20px currentColor; }
        }

        @keyframes borderPulse {
          0%, 100% { opacity: 0.6; transform: scaleY(1); }
          50% { opacity: 1; transform: scaleY(1.05); }
        }

        @keyframes featureShine {
          0% { left: '-100%'; }
          100% { left: '100%'; }
        }

        @keyframes buttonShine {
          0% { left: '-100%'; }
          50% { left: '100%'; }
          100% { left: '100%'; }
        }

        @keyframes imageGlowPulse {
          0%, 100% { 
            opacity: 0.8; 
            transform: scale(1) rotate(0deg); 
          }
          50% { 
            opacity: 1; 
            transform: scale(1.1) rotate(180deg); 
          }
        }

        @keyframes imageShimmer {
          0% { 
            opacity: 0.6; 
            transform: translateX(-20px) translateY(-10px); 
          }
          50% { 
            opacity: 0.9; 
            transform: translateX(20px) translateY(10px); 
          }
          100% { 
            opacity: 0.6; 
            transform: translateX(-20px) translateY(-10px); 
          }
        }

        /* Icon Animations */
        @keyframes iconBounceIn {
          0% { transform: scale(0) rotate(0deg); opacity: 0; }
          50% { transform: scale(1.3) rotate(180deg); opacity: 0.8; }
          100% { transform: scale(1) rotate(360deg); opacity: 1; }
        }

        @keyframes iconSpinIn {
          0% { transform: scale(0.3) rotate(-360deg); opacity: 0; }
          70% { transform: scale(1.1) rotate(0deg); opacity: 0.9; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }

        @keyframes iconZoomIn {
          0% { transform: scale(0) translateY(50px); opacity: 0; }
          60% { transform: scale(1.2) translateY(-10px); opacity: 0.8; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }

        @keyframes iconFlipIn {
          0% { transform: rotateY(90deg) scale(0.5); opacity: 0; }
          50% { transform: rotateY(45deg) scale(1.2); opacity: 0.8; }
          100% { transform: rotateY(0deg) scale(1); opacity: 1; }
        }

        @keyframes iconSlideIn {
          0% { transform: translateX(-100px) scale(0.7); opacity: 0; }
          70% { transform: translateX(10px) scale(1.1); opacity: 0.9; }
          100% { transform: translateX(0) scale(1); opacity: 1; }
        }

        /* Soft Title Animations */
        @keyframes titleSoftFade {
          0% { 
            transform: translateY(20px); 
            opacity: 0; 
            filter: blur(2px); 
          }
          100% { 
            transform: translateY(0); 
            opacity: 1; 
            filter: blur(0); 
          }
        }

        @keyframes titleGentleRise {
          0% { 
            transform: translateY(30px) scale(0.95); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
        }

        @keyframes titleSmoothScale {
          0% { 
            transform: scale(0.9); 
            opacity: 0; 
          }
          100% { 
            transform: scale(1); 
            opacity: 1; 
          }
        }

        /* Soft Subtitle Animations */
        @keyframes subtitleSoftSlide {
          0% { 
            transform: translateX(25px); 
            opacity: 0; 
          }
          100% { 
            transform: translateX(0); 
            opacity: 1; 
          }
        }

        @keyframes subtitleGentleFade {
          0% { 
            transform: translateY(15px) scale(0.98); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
        }

        @keyframes subtitleSmoothGrow {
          0% { 
            transform: scale(0.95); 
            opacity: 0; 
            filter: blur(1px); 
          }
          100% { 
            transform: scale(1); 
            opacity: 1; 
            filter: blur(0); 
          }
        }

        /* Feature Animations */
        @keyframes slideUpBounce {
          0% { transform: translateY(80px) scale(0.8); opacity: 0; }
          60% { transform: translateY(-20px) scale(1.1); opacity: 0.8; }
          80% { transform: translateY(5px) scale(0.95); opacity: 0.9; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }

        @keyframes slideUpScale {
          0% { transform: translateY(60px) scale(0.5); opacity: 0; }
          70% { transform: translateY(-10px) scale(1.15); opacity: 0.8; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }

        @keyframes slideUpRotate {
          0% { transform: translateY(70px) rotateZ(15deg) scale(0.7); opacity: 0; }
          60% { transform: translateY(-15px) rotateZ(-5deg) scale(1.05); opacity: 0.8; }
          100% { transform: translateY(0) rotateZ(0deg) scale(1); opacity: 1; }
        }

        @keyframes slideUpFlip {
          0% { transform: translateY(60px) rotateX(90deg); opacity: 0; }
          50% { transform: translateY(-10px) rotateX(45deg); opacity: 0.8; }
          100% { transform: translateY(0) rotateX(0deg); opacity: 1; }
        }

        @keyframes slideUpWave {
          0% { transform: translateY(80px) skewY(10deg); opacity: 0; }
          60% { transform: translateY(-20px) skewY(-5deg); opacity: 0.8; }
          100% { transform: translateY(0) skewY(0deg); opacity: 1; }
        }

        /* Soft Feature Animations */
        @keyframes featureSoftRise {
          0% { 
            transform: translateY(25px); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0); 
            opacity: 1; 
          }
        }

        @keyframes featureGentleFade {
          0% { 
            transform: translateY(20px) scale(0.98); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
        }

        @keyframes featureSmoothSlide {
          0% { 
            transform: translateY(30px) translateX(10px); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0) translateX(0); 
            opacity: 1; 
          }
        }

        /* Soft Button Animation */
        @keyframes buttonsSoftRise {
          0% { 
            transform: translateY(20px) scale(0.98); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
        }

        /* Soft Stats Animation */
        @keyframes statsSoftFade {
          0% { 
            transform: translateY(15px) scale(0.96); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0) scale(1); 
            opacity: 1; 
          }
        }

        /* Soft Description Animation */
        @keyframes descriptionSoftFade {
          0% { 
            transform: translateY(18px); 
            opacity: 0; 
          }
          100% { 
            transform: translateY(0); 
            opacity: 1; 
          }
        }
      `}</style>
    </Box>
  );
};

export default HeroRevolution;